import React, { useState, useEffect } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import * as api from '../utils/api/apiClient'
import { Loading } from './Loading'

const CustomCalendar = () => {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedEventId, setSelectedEventId] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [newEvent, setNewEvent] = useState({
    name: '',
    at: '',
  })
  const [holiydaysFilter, setHoliydaysFilter] = useState({
    from: '',
    to: '',
  })
  const [holidays, setholidays] = useState([])

  // Transform holidays data to FullCalendar event format
  const transformedHolidays = holidays.map((holiday) => ({
    title: holiday.name,
    date: holiday.at,
    backgroundColor: '#4ECDC4',
    borderColor: '#4ECDC4',
    id: holiday.id, // Make sure the ID is included
  }))

  // Combine static events with transformed holidays
  const allEvents = [...events, ...transformedHolidays]

  const formatDate = (dateString) => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString
    }
    const date = new Date(dateString)
    return date.toISOString().split('T')[0]
  }

  const handleDateClick = (info) => {
    setSelectedDate(formatDate(info.dateStr))
    setIsModalOpen(true)
  }

  // New function to handle event clicks
  const handleEventClick = (info) => {
    setSelectedEventId(info.event.id)
    setIsOpen(true)
  }

  const handleCancelDelete = () => {
    setIsOpen(false)
    setSelectedEventId(null)
  }

  const deleteHoliyday = async () => {
    if (!selectedEventId) return

    try {
      await api.deleteHolidays(selectedEventId)

      // State update with fresh data
      const updatedHolidays = await api.getHolidays(
        holiydaysFilter.from,
        holiydaysFilter.to
      )
      setholidays(updatedHolidays.data)

      setIsOpen(false)
      setSelectedEventId(null)
    } catch (error) {
      console.log(error)
      alert('Failed to delete event')
    }
  }

  const handleSubmitEvent = async () => {
    if (!newEvent.name.trim()) {
      alert('Please enter an event name')
      return
    }

    try {
      const eventToAdd = {
        name: newEvent.name,
        at: selectedDate,
      }

      const res = await api.addHolidays(eventToAdd)

      // State update with new data
      const newHolidays = await api.getHolidays(
        holiydaysFilter.from,
        holiydaysFilter.to
      )
      setholidays(newHolidays.data)

      setIsModalOpen(false)
      setNewEvent({ name: '', at: '' })
    } catch (error) {
      console.error('Error submitting event:', error)
      alert('Failed to add event. Please try again.')
    }
  }

  const handleDatesSet = (dateInfo) => {
    const startDate = formatDate(dateInfo.startStr)
    const endDate = formatDate(dateInfo.endStr)

    setHoliydaysFilter({
      from: startDate,
      to: endDate,
    })

    const fetchHoliydays = async () => {
      try {
        const res = await api.getHolidays(startDate, endDate)
        setholidays(res.data)
      } catch (error) {
        console.log(error)
      }
    }
    fetchHoliydays()
  }

  useEffect(() => {
    const fetchHoliydays = async () => {
      try {
        const res = await api.getHolidays(
          holiydaysFilter.from,
          holiydaysFilter.to
        )
        setholidays(res.data)
      } catch (error) {
        console.log(error)
      }
    }
    fetchHoliydays()

    setTimeout(() => {
      setLoading(true)
    }, 400)
  }, [holiydaysFilter])

  return (
    <div className='p-4'>
      {!loading && <Loading />}
      <FullCalendar
        height='500px'
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView='dayGridMonth'
        events={allEvents}
        dateClick={handleDateClick}
        eventClick={handleEventClick}
        eventDisplay='block'
        datesSet={handleDatesSet}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,dayGridWeek,dayGridDay',
        }}
      />

      {isModalOpen && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50'>
          <div className='bg-white p-6 rounded-lg shadow-xl w-96'>
            <h2 className='text-xl font-bold mb-4'>Add New Event</h2>
            <div className='mb-4'>
              <label className='block mb-2 font-medium'>Event Name</label>
              <input
                type='text'
                placeholder='Enter event name'
                value={newEvent.name}
                onChange={(e) =>
                  setNewEvent({ ...newEvent, name: e.target.value })
                }
                className='w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                required
              />
            </div>

            <div className='flex justify-end space-x-2'>
              <button
                onClick={() => setIsModalOpen(false)}
                className='px-4 py-2 bg-rust text-white rounded hover:bg-rust/50 transition'
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitEvent}
                className='px-4 py-2 bg-link text-white rounded hover:bg-link/50 transition'
              >
                Add Event
              </button>
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <div className='fixed inset-0  z-40 flex items-center justify-center bg-black bg-opacity-50'>
          <div className='bg-cream p-6 rounded-lg shadow-lg w-96'>
            <h2 className='text-lg font-semibold text-black mb-4'>
              Are you sure?
            </h2>
            <p className='text-sm text-muted_green mb-6'>
              Do you want to proceed with this action?
            </p>
            <div className='flex justify-end space-x-4'>
              <button
                onClick={handleCancelDelete}
                className='px-4 py-2 bg-tan text-white rounded hover:bg-beige transition'
              >
                Cancel
              </button>
              <button
                onClick={deleteHoliyday}
                className='px-4 py-2 bg-rust text-white rounded hover:bg-terracotta transition'
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomCalendar
